import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Field, reduxForm } from 'redux-form/immutable';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import { createStructuredSelector } from 'reselect';
import moment from 'moment';
import { debounce, find, get, isString } from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import { Avatar, Button, Dialog, DialogActions, DialogContent, Typography } from '@material-ui/core';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon, Warning as WarningIcon } from '@material-ui/icons';

import { stringToColor, getInitials } from 'ui-utils';
import { createValidator, required } from 'utils/validation';
import { makeSelectFormField } from 'containers/App/selectors';
import { DialogHeader } from 'embeds/components/DialogHeader';
import FormPatientSearchField from 'embeds/components/ReduxForm/FormPatientSearchField';
import CustomPatientOption from './CustomPatientOption';

import { makeSelectOpen, makeSelectPatients, makeSelectSuccess } from './selectors';
import { closeDialog, mergePatients, searchPatients } from './actions';
import { FORM_NAME } from './constants';

const styles = ({
  custom: {
    palette: { colorDanger, colorDangerHover, colorSuccess, colorSupportSlate },
    fontSizes,
  },
  palette: {
    common: { white },
    text,
  },
  spacing: { unit },
}) => ({
  alertContainer: {
    border: `1px solid ${colorDanger}`,
    display: 'inline-block',
    marginTop: unit * 2,
    padding: unit * 2,
    width: '100%',
  },
  alertIconContainer: {
    display: 'inline-block',
    float: 'left',
    marginRight: unit,
  },
  alertIcon: {
    color: colorDanger,
    fontSize: fontSizes.bodyS,
  },
  alertText: {
    color: text.primary,
    display: 'inline-block',
    float: 'left',
    fontWeight: 500,
    lineHeight: '24px',
    width: 'calc(100% - 26px)',
    '& > a': {
      color: text.primary,
    },
  },
  avatar: {
    color: `${white} !important`,
    fontSize: fontSizes.bodyS,
    marginRight: unit * 2,
  },
  buttonConfirm: {
    backgroundColor: colorDanger,
    '&:hover': {
      backgroundColor: colorDangerHover,
    },
  },
  content: {
    minHeight: 268,
    padding: unit * 3,
  },
  duplicatedPatient: {
    color: text.primary,
    fontWeight: 600,
    lineHeight: '32px',
    margin: `${unit * 2}px 0px`,
    maxHeight: 162,
    overflow: 'auto',
  },
  field: {
    marginBottom: unit * 2,
    marginTop: unit * 2,
    '& .Select': {
      width: '100% !important',
    },
    '& .Select-menu-outer': {
      boxShadow: 'none',
    },
    '& .Select-menu-outer > div': {
      maxHeight: 138,
    },
  },
  fieldClearIcon: {
    color: text.primary,
    fontSize: fontSizes.small,
  },
  footer: {
    textAlign: 'right',
  },
  form: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  successIcon: {
    color: colorSuccess,
    fontSize: fontSizes.bodyS,
    marginRight: unit,
  },
  text: {
    fontWeight: 500,
  },
  tip: {
    color: text.primary,
    marginTop: unit * 2,
  },
  wrapper: {
    borderRadius: 0,
    width: 608,
  },
});

const validate = createValidator({
  idToKeep: required,
  idsToTrash: required,
});

class DialogPatientMerge extends PureComponent {
  constructor(props) {
    super(props);

    this.renderContent = this.renderContent.bind(this);

    this.state = {
      currentStep: 0,
      invalidPatient: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    const { initialize, open, success } = this.props;

    if (nextProps.open && nextProps.open !== open) {
      this.setState({ currentStep: 0 });

      initialize({ idToKeep: '', idsToTrash: nextProps.patientToMerge._id || false });
    }

    if (!success && nextProps.success) this.setState({ currentStep: 2 });
  }

  handleDialogClose = () => {
    const { dispatchCloseDialog, idToKeep, onRequestClose } = this.props;
    const { currentStep } = this.state;

    if (onRequestClose) onRequestClose(currentStep, idToKeep);

    dispatchCloseDialog();
  };

  handlePatientChange = (value) => {
    const { dispatchChange, patients, patientToMerge } = this.props;
    const patientToKeep = value && find(patients, { value });

    dispatchChange('idToKeep', value);

    const invalidPatient =
      get(patientToKeep, 'mergedInto') ||
      (get(patientToMerge, 'externalId.value') &&
        get(patientToMerge, 'secondaryExternalId.value') &&
        get(patientToKeep, 'externalId') &&
        get(patientToKeep, 'secondaryExternalId'));

    this.setState({ invalidPatient });
  };

  handleSearchPatients = (text) => {
    const { dispatchSearchPatients, patientToMerge } = this.props;

    dispatchSearchPatients(patientToMerge ? patientToMerge._id : false, text);
  };

  handleSubmit = (data) => {
    const { dispatchMergePatients } = this.props;
    const body = data.toJS();

    body.idsToTrash = [body.idsToTrash];

    dispatchMergePatients(body);
  };

  handleSubmitButtonClick = () => {
    const { dispatchCloseDialog } = this.props;
    const { currentStep } = this.state;

    if (currentStep === 2) {
      dispatchCloseDialog();
    } else {
      this.setState({ currentStep: 1 });
    }
  };

  renderFooter() {
    const { classes, handleSubmit, pristine, submitting, valid } = this.props;
    const { currentStep, invalidPatient } = this.state;
    let submitLabel = '';

    switch (currentStep) {
      case 1:
        submitLabel = 'Yes, delete and merge';
        break;
      case 2:
        submitLabel = 'Close';
        break;
      default:
        submitLabel = 'Next';
    }

    return (
      <DialogActions>
        {currentStep !== 2 && (
          <Button color="primary" form={FORM_NAME} onClick={this.handleDialogClose} variant="text">
            Cancel
          </Button>
        )}
        {currentStep !== 1 && (
          <Button
            color="primary"
            disabled={invalidPatient || (!submitting && (pristine || !valid)) || submitting}
            form={FORM_NAME}
            onClick={currentStep === 2 ? this.handleDialogClose : this.handleSubmitButtonClick}
            variant="contained"
          >
            {submitLabel}
          </Button>
        )}
        {currentStep === 1 && (
          <Button
            className={classes.buttonConfirm}
            color="primary"
            disabled={(!submitting && (pristine || !valid)) || submitting}
            form={FORM_NAME}
            onClick={handleSubmit(this.handleSubmit)}
            variant="contained"
          >
            {submitLabel}
          </Button>
        )}
      </DialogActions>
    );
  }

  renderInvalidPatientAlert() {
    const { classes } = this.props;
    const { invalidPatient } = this.state;

    if (!invalidPatient) return;

    return (
      <div className={classes.alertContainer}>
        <div className={classes.alertIconContainer}>
          <WarningIcon className={classes.alertIcon} />
        </div>
        <Typography className={classes.alertText} color="primary" variant="body2">
          We cannot merge these two entries &mdash; they&rsquo;re both linked to your EHR. Please contact&nbsp;
          <a href="mailto:support@lumahealth.io">support@lumahealth.io</a> for assistance
        </Typography>
      </div>
    );
  }

  renderFieldIcon() {
    const { classes } = this.props;

    return <CloseIcon className={classes.fieldClearIcon} />;
  }

  renderFieldSelectedValue({ children }) {
    const { classes, patientToMerge } = this.props;
    const [name, dob, updatedAt] = children.split('||');
    const initials = getInitials(name);
    const backgroundColor = stringToColor(name);
    let dateOfBirth = dob;
    let updatedDate = updatedAt;

    // is the patient to be merged
    if (!dateOfBirth && !updatedDate) {
      updatedDate = moment(patientToMerge.updatedAt).format('M/D/YYYY') || '';

      if (patientToMerge.dateOfBirth) {
        const { day, month, year } = patientToMerge.dateOfBirth;

        dateOfBirth = isString(patientToMerge.dateOfBirth) ? patientToMerge.dateOfBirth : `${month}/${day}/${year}`;
        dateOfBirth = (dateOfBirth && moment(dateOfBirth).format('M/D/YYYY')) || '';
      }
    }

    return (
      <div className="Select-value">
        <Avatar className={classes.avatar} size={35} style={{ backgroundColor }}>
          {initials}
        </Avatar>
        {`${name}${dateOfBirth !== '' ? ` - ${dateOfBirth}` : ''}${
          updatedDate !== '' ? ` - Last edited ${updatedDate}` : ''
        }`}
      </div>
    );
  }

  renderContent() {
    const { classes, handleSubmit, patients, patientToMerge } = this.props;
    const { currentStep } = this.state;
    const slowPatientsSearch = debounce(this.handleSearchPatients, 300);
    let content = false;
    if (!patientToMerge) return;

    switch (currentStep) {
      case 2:
        content = (
          <DialogContent className={classes.content}>
            <Typography className={classes.text} color="primary" variant="body1">
              <CheckCircleIcon className={classes.successIcon} />
              Success! The entry for <b>{patientToMerge.name}</b> has been deleted and merged.
            </Typography>
            <br />
            <Typography className={classes.text} color="primary" variant="body1">
              <b>kept:</b>
            </Typography>
            <Field
              className={classes.field}
              clearIconRenderer={this.renderFieldIcon()}
              component={FormPatientSearchField}
              disabled
              label=""
              name="idToKeep"
              onChange={(event, value) => this.handlePatientChange(value)}
              onFilter={slowPatientsSearch}
              optionRenderer={CustomPatientOption}
              options={patients || []}
              valueRenderer={(props) => this.renderFieldSelectedValue(props)}
            />
            <Typography className={classes.text} color="primary" variant="body1">
              <b>Deleted and merged:</b>
            </Typography>
            <Field
              className={classes.field}
              clearIconRenderer={this.renderFieldIcon()}
              component={FormPatientSearchField}
              disabled
              label=""
              name="idsToTrash"
              optionRenderer={CustomPatientOption}
              options={[patientToMerge]}
              valueRenderer={(props) => this.renderFieldSelectedValue(props)}
            />
          </DialogContent>
        );
        break;
      case 1:
        content = (
          <DialogContent className={classes.content}>
            <Typography className={classes.text} color="primary" variant="body1">
              To be <b>kept:</b>
            </Typography>
            <Field
              className={classes.field}
              clearIconRenderer={this.renderFieldIcon()}
              component={FormPatientSearchField}
              disabled
              label=""
              name="idToKeep"
              onChange={(event, value) => this.handlePatientChange(value)}
              onFilter={slowPatientsSearch}
              optionRenderer={CustomPatientOption}
              options={patients || []}
              valueRenderer={(props) => this.renderFieldSelectedValue(props)}
            />
            <Typography className={classes.text} color="primary" variant="body1">
              To be <b>deleted and merged:</b>
            </Typography>
            <Field
              className={classes.field}
              clearIconRenderer={this.renderFieldIcon()}
              component={FormPatientSearchField}
              disabled
              label=""
              name="idsToTrash"
              onChange={(event, value) => this.handlePatientChange(value)}
              onFilter={slowPatientsSearch}
              optionRenderer={CustomPatientOption}
              options={[patientToMerge]}
              valueRenderer={(props) => this.renderFieldSelectedValue(props)}
            />
            <Typography className={classes.text} color="primary" variant="body1">
              <b>
                You will not be able to undo this — please double check that everything is correct before proceeding.
              </b>
            </Typography>
          </DialogContent>
        );
        break;
      default:
        content = (
          <DialogContent className={classes.content}>
            <Typography className={classes.text} color="primary" variant="body1">
              Which patient entry should replace <b>{patientToMerge.name}</b>?
            </Typography>
            <Field
              className={classes.field}
              clearIconRenderer={this.renderFieldIcon()}
              component={FormPatientSearchField}
              label=""
              name="idToKeep"
              onChange={(event, value) => this.handlePatientChange(value)}
              onFilter={slowPatientsSearch}
              optionRenderer={CustomPatientOption}
              options={patients || []}
              placeholder="Look up existing patient"
              valueRenderer={(props) => this.renderFieldSelectedValue(props)}
            />
            {this.renderInvalidPatientAlert()}
          </DialogContent>
        );
    }

    return (
      <form autoComplete="off" id={FORM_NAME} className={classes.form} onSubmit={handleSubmit(this.handleSubmit)}>
        {content}
      </form>
    );
  }

  render() {
    const { classes, open } = this.props;

    return (
      <Dialog classes={{ paper: classes.wrapper }} onClose={this.handleDialogClose} open={open}>
        <DialogHeader leftIcon="supervisor_account" onRequestClose={this.handleDialogClose} title="Merge Patients" />
        {this.renderContent()}
        {this.renderFooter()}
      </Dialog>
    );
  }
}

DialogPatientMerge.propTypes = {
  classes: PropTypes.object.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  dispatchCloseDialog: PropTypes.func.isRequired,
  dispatchMergePatients: PropTypes.func.isRequired,
  dispatchSearchPatients: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  idToKeep: PropTypes.string,
  initialize: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func,
  open: PropTypes.bool,
  patientToMerge: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
  patients: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  success: PropTypes.bool,
  valid: PropTypes.bool.isRequired,
};

DialogPatientMerge.defaultProps = {
  onRequestClose: undefined,
  open: false,
  patients: false,
  success: false,
};

const mapStateToProps = createStructuredSelector({
  idToKeep: makeSelectFormField(FORM_NAME, 'idToKeep'),
  open: makeSelectOpen(),
  patients: makeSelectPatients(),
  success: makeSelectSuccess(),
});

const mapDispatchToProps = (dispatch) => ({
  dispatchChange: (field, value) => {
    dispatch(change(FORM_NAME, field, value));
  },
  dispatchCloseDialog: () => {
    dispatch(closeDialog());
  },
  dispatchMergePatients: (body) => {
    dispatch(mergePatients(body));
  },
  dispatchSearchPatients: (patientId, text) => {
    dispatch(searchPatients(patientId, text));
  },
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withForm = reduxForm({ form: FORM_NAME, validate });

export default compose(withConnect, withForm, withStyles(styles))(DialogPatientMerge);
